import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { imageUrl, imageSrcSet } from '../api';

// 03_requirements_missing.md story 2 - a property's photos as a slideshow (prev/next, dots,
// keyboard arrows), and a zoomable lightbox when a slide is clicked. No external carousel/lightbox
// library - react-bootstrap's Carousel doesn't do zoom/pan, and pulling in a lightbox dep for one
// screen isn't worth it.
export default function PhotoGallery({ images = [], alt = 'Property photo' }) {
  const photos = (images || []).filter(Boolean);
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const safeIndex = photos.length ? Math.min(index, photos.length - 1) : 0;

  const go = useCallback((delta) => {
    setIndex((i) => {
      const n = photos.length;
      if (!n) return 0;
      return (i + delta + n) % n;
    });
  }, [photos.length]);

  if (photos.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light rounded text-muted" style={{ height: 320 }}>
        No photos yet
      </div>
    );
  }

  return (
    <div>
      <div className="position-relative rounded overflow-hidden shadow-sm" style={{ background: '#000' }}>
        <img
          src={imageUrl(photos[safeIndex])}
          srcSet={imageSrcSet(photos[safeIndex]) || undefined}
          sizes="(max-width: 768px) 100vw, 66vw"
          alt={`${alt} ${safeIndex + 1} of ${photos.length}`}
          onClick={() => setLightboxOpen(true)}
          loading={safeIndex === 0 ? 'eager' : 'lazy'}
          fetchPriority={safeIndex === 0 ? 'high' : undefined}
          decoding="async"
          className="w-100"
          style={{ height: 360, objectFit: 'contain', cursor: 'zoom-in', display: 'block' }}
        />
        {photos.length > 1 && (
          <>
            <Button
              variant="light" size="sm"
              className="position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle opacity-75"
              style={{ width: 36, height: 36 }}
              onClick={() => go(-1)}
              aria-label="Previous photo"
            >
              ‹
            </Button>
            <Button
              variant="light" size="sm"
              className="position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle opacity-75"
              style={{ width: 36, height: 36 }}
              onClick={() => go(1)}
              aria-label="Next photo"
            >
              ›
            </Button>
            <span className="position-absolute bottom-0 end-0 m-2 badge bg-dark bg-opacity-75">
              {safeIndex + 1} / {photos.length}
            </span>
          </>
        )}
      </div>

      {photos.length > 1 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {photos.map((src, i) => (
            <img
              key={src}
              src={imageUrl(src)}
              srcSet={imageSrcSet(src) || undefined}
              sizes="64px"
              alt={`${alt} thumbnail ${i + 1}`}
              loading="lazy"
              decoding="async"
              onClick={() => setIndex(i)}
              style={{
                width: 64, height: 64, objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                outline: i === safeIndex ? '2px solid var(--bs-primary)' : '1px solid #dee2e6'
              }}
            />
          ))}
        </div>
      )}

      {lightboxOpen && (
        <Lightbox
          photos={photos}
          index={safeIndex}
          onIndex={setIndex}
          alt={alt}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

function Lightbox({ photos, index, onIndex, alt, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragState = useRef(null);

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

  useEffect(() => { resetView(); }, [index, resetView]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onIndex((i) => (i - 1 + photos.length) % photos.length);
      else if (e.key === 'ArrowRight') onIndex((i) => (i + 1) % photos.length);
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(MAX_ZOOM, z + 0.5));
      else if (e.key === '-') setZoom((z) => Math.max(MIN_ZOOM, z - 0.5));
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [photos.length, onClose, onIndex]);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + (e.deltaY < 0 ? 0.3 : -0.3))));
  };

  const handleImageClick = () => {
    // click toggles between fit and 2.5x, centred
    setPan({ x: 0, y: 0 });
    setZoom((z) => (z > 1 ? 1 : 2.5));
  };

  const onPointerDown = (e) => {
    if (zoom <= 1) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, origin: { ...pan } };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    setPan({
      x: dragState.current.origin.x + (e.clientX - dragState.current.startX),
      y: dragState.current.origin.y + (e.clientY - dragState.current.startY)
    });
  };
  const onPointerUp = () => { dragState.current = null; };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
      style={{ background: 'rgba(0,0,0,0.92)', zIndex: 2000 }}
      onClick={onClose}
    >
      <div className="d-flex justify-content-between align-items-center p-2 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="small">{index + 1} / {photos.length}</span>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-light" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 0.5))} aria-label="Zoom out">−</Button>
          <Button size="sm" variant="outline-light" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.5))} aria-label="Zoom in">+</Button>
          <Button size="sm" variant="outline-light" onClick={resetView}>Reset</Button>
          <Button size="sm" variant="light" onClick={onClose} aria-label="Close">✕</Button>
        </div>
      </div>

      <div
        className="flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden position-relative"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
      >
        <img
          src={imageUrl(photos[index])}
          srcSet={imageSrcSet(photos[index]) || undefined}
          sizes="100vw"
          alt={`${alt} ${index + 1}`}
          onClick={handleImageClick}
          draggable={false}
          decoding="async"
          style={{
            maxHeight: '100%', maxWidth: '100%', objectFit: 'contain',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: dragState.current ? 'none' : 'transform 0.15s ease-out',
            cursor: zoom > 1 ? 'grab' : 'zoom-in'
          }}
        />
        {photos.length > 1 && (
          <>
            <Button
              variant="light"
              className="position-absolute top-50 start-0 translate-middle-y ms-3 rounded-circle opacity-75"
              style={{ width: 44, height: 44 }}
              onClick={() => onIndex((i) => (i - 1 + photos.length) % photos.length)}
              aria-label="Previous photo"
            >
              ‹
            </Button>
            <Button
              variant="light"
              className="position-absolute top-50 end-0 translate-middle-y me-3 rounded-circle opacity-75"
              style={{ width: 44, height: 44 }}
              onClick={() => onIndex((i) => (i + 1) % photos.length)}
              aria-label="Next photo"
            >
              ›
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
